// @ts-nocheck
// TODO: tighten types after migration
import React, { useState, useEffect, useCallback } from "react";
import "./FullScreenImageViewer.css";

import { createPortal } from "react-dom";

const FullScreenImageViewer = ({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
  triggerRect = null,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    
    // Animation States
    const [isAnimating, setIsAnimating] = useState(false);
    const [overlayOpacity, setOverlayOpacity] = useState(0);
    const [contentOpacity, setContentOpacity] = useState(0);
    const [isReady, setIsReady] = useState(false); 

    const imageRef = React.useRef(null);
    const dragStart = React.useRef({ x: 0, y: 0 });
    const initialDistance = React.useRef(0);
    const initialScale = React.useRef(1);
    const scrollYRef = React.useRef(0);

    // Update index when initialIndex changes or modal opens
    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
        }
    }, [isOpen, initialIndex]);

    // Reset zoom when image changes
    useEffect(() => {
        setScale(1);
        setPosition({ x: 0, y: 0 });
    }, [currentIndex]);

    // Handle Scroll Locking and Entrance Transition
    useEffect(() => {
        if (isOpen) {
            scrollYRef.current = window.scrollY;
            
            // Standard scroll lock
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            
            setIsAnimating(true);
            
            // Allow one frame for the Portal to mount and layout to happen
            setTimeout(() => {
                setOverlayOpacity(1);
                if (triggerRect && imageRef.current) {
                    const rect = triggerRect;
                    const img = imageRef.current;
                    
                    // Initial sync
                    img.style.opacity = '0';
                    requestAnimationFrame(() => {
                        const targetRect = img.getBoundingClientRect();
                        
                        const dx = (rect.left + rect.width / 2) - (targetRect.left + targetRect.width / 2);
                        const dy = (rect.top + rect.height / 2) - (targetRect.top + targetRect.height / 2);
                        const sw = rect.width / targetRect.width;
                        const sh = rect.height / targetRect.height;
                        const s = Math.max(sw, sh);
                        
                        img.style.transition = 'none';
                        img.style.transform = `translate(${dx}px, ${dy}px) scale(${s})`;
                        img.style.opacity = '1';
                        setIsReady(true);
                        
                        requestAnimationFrame(() => {
                            img.style.transition = 'transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)';
                            img.style.transform = 'translate(0, 0) scale(1)';
                            setContentOpacity(1);
                            
                            setTimeout(() => {
                                setIsAnimating(false);
                                if (img) {
                                    img.style.transition = '';
                                    img.style.transform = '';
                                }
                            }, 500);
                        });
                    });
                } else {
                    setIsReady(true);
                    setContentOpacity(1);
                    setIsAnimating(false);
                }
            }, 50);

            return () => {
                document.body.style.overflow = originalStyle;
                window.scrollTo(0, scrollYRef.current);
            };
        }
    }, [isOpen, triggerRect]);

    const handleNext = (e) => {
        if (e) e.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrev = (e) => {
        if (e) e.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleClose = useCallback((e) => {
        if (e) e.stopPropagation();
        if (isAnimating) return;

        if (triggerRect && imageRef.current) {
            setIsAnimating(true);
            setContentOpacity(0);
            setOverlayOpacity(0);

            const rect = triggerRect;
            const img = imageRef.current;
            const targetRect = img.getBoundingClientRect();

            const dx = (rect.left + rect.width / 2) - (targetRect.left + targetRect.width / 2);
            const dy = (rect.top + rect.height / 2) - (targetRect.top + targetRect.height / 2);
            const sw = rect.width / targetRect.width;
            const sh = rect.height / targetRect.height;
            const s = Math.max(sw, sh);

            img.style.transition = 'transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1)';
            img.style.transform = `translate(${dx}px, ${dy}px) scale(${s})`;

            setTimeout(() => {
                onClose();
                setIsAnimating(false);
                setIsReady(false);
            }, 500);
        } else {
            setOverlayOpacity(0);
            setContentOpacity(0);
            setTimeout(onClose, 300);
        }
    }, [isAnimating, triggerRect, onClose]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {
                handleClose();
                return;
            }
            if (!images || images.length === 0) return;
            
            if (e.key === 'ArrowRight') {
                if (e) e.stopPropagation();
                setCurrentIndex((prev) => (prev + 1) % images.length);
            }
            if (e.key === 'ArrowLeft') {
                if (e) e.stopPropagation();
                setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, images, isAnimating, handleClose]); 

    if (!isOpen || !images || images.length === 0) return null;

    const currentImage = images[currentIndex]; 

    const handleWheel = (e) => {
        if (isAnimating) return;
        e.preventDefault();
        const delta = e.deltaY * -0.01;
        const newScale = Math.min(Math.max(1, scale + delta), 4);
        setScale(newScale);
        if (newScale === 1) setPosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
        if (scale > 1 && !isAnimating) {
            e.preventDefault();
            setIsDragging(true);
            dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && scale > 1 && !isAnimating) {
            e.preventDefault();
            const x = e.clientX - dragStart.current.x;
            const y = e.clientY - dragStart.current.y;
            setPosition({ x, y });
        }
    };

    const handleMouseUp = () => setIsDragging(false);

    const handleTouchStart = (e) => {
        if (isAnimating) return;
        if (e.touches.length === 2) {
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            initialDistance.current = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
            initialScale.current = scale;
        } else if (e.touches.length === 1 && scale > 1) {
            setIsDragging(true);
            dragStart.current = { x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y };
        }
    };

    const handleTouchMove = (e) => {
        if (isAnimating) return;
        if (e.touches.length === 2) {
            e.preventDefault();
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            const dist = Math.hypot(touch1.clientX - touch2.clientX, touch1.clientY - touch2.clientY);
            if (initialDistance.current > 0) {
                const scaleFactor = dist / initialDistance.current;
                const newScale = Math.min(Math.max(1, initialScale.current * scaleFactor), 4);
                setScale(newScale);
                if (newScale === 1) setPosition({ x: 0, y: 0 });
            }
        } else if (e.touches.length === 1 && scale > 1 && isDragging) {
            e.preventDefault();
            const x = e.touches[0].clientX - dragStart.current.x;
            const y = e.touches[0].clientY - dragStart.current.y;
            setPosition({ x, y });
        }
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        initialDistance.current = 0;
    };

    const viewerContent = (
        <div
            className="fullscreen-overlay fixed inset-0 w-screen h-screen bg-[rgba(0,0,0,0.98)] z-[2000000000] backdrop-blur-[10px] overflow-hidden cursor-default"
            style={{
                opacity: overlayOpacity,
                transition: 'opacity 0.5s ease'
            }}
            onClick={() => handleClose()}
        >
            <div className="fullscreen-content absolute inset-0 overflow-hidden">

                <div
                    className="fullscreen-header absolute top-0 left-0 w-full px-[clamp(15px,4vw,25px)] py-[clamp(15px,4vw,25px)] flex justify-end items-center z-[100] bg-gradient-to-b from-[rgba(0,0,0,0.5)] to-transparent pointer-events-none"
                    style={{
                        opacity: contentOpacity,
                        transform: `translateY(${contentOpacity === 1 ? '0' : '-30px'})`,
                        transition: 'opacity 0.4s ease, transform 0.4s ease'
                    }}
                >
                      <div className="closebtn relative pointer-events-auto flex flex-row items-center cursor-pointer bg-[rgba(255,255,255,0.1)] px-3 py-1.5 rounded-[12px] backdrop-blur-[15px] border-none shadow-[0_4px_30px_rgba(0,0,0,0.5)]" onClick={() => handleClose()}>
                        <i id="close_btn" className="fi fi-rr-cross-small text-orange-400 text-[16px]" style={{ marginRight: '4px' }}></i>
                        <span style={{ color: '#fff', fontSize: '13px', marginRight: '8px', fontWeight: 500 }}>Close</span>
                        <span className="shortcut-badge">Esc</span>
                      </div>
                </div>

                <div
                    className="fullscreen-body fixed inset-0 w-screen h-screen flex justify-center items-center p-0 m-0 overflow-hidden z-[5] pointer-events-none"
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {currentImage.isVideo ? (
                        <video
                            src={currentImage.src || currentImage.url}
                            controls
                            autoPlay
                            playsInline
                            className="fullscreen-video pointer-events-auto max-w-[90vw] max-h-[80vh] object-contain block m-0"
                            style={{
                                opacity: isReady ? 1 : 0,
                                zIndex: 10,
                                borderRadius: '8px',
                                outline: 'none'
                            }}
                        />
                    ) : (
                        <img
                            ref={imageRef}
                            src={currentImage.src || currentImage.url}
                            alt={currentImage.alt || ''}
                            className="fullscreen-image pointer-events-auto max-w-full max-h-full object-contain block m-0"
                            style={{
                                transform: !isAnimating ? `translate(${position.x}px, ${position.y}px) scale(${scale})` : undefined,
                                transition: isDragging || isAnimating ? 'none' : 'transform 0.1s ease-out',
                                opacity: isReady ? 1 : 0,
                                pointerEvents: scale > 1 ? 'auto' : 'none'
                            }}
                            draggable={false}
                        />
                    )}
                </div>

                <div
                    className="fullscreen-footer absolute bottom-0 left-0 w-full px-[30px] py-[60px_30px_30px] flex flex-row justify-between items-end bg-gradient-to-t from-[rgba(0,0,0,0.7)] to-transparent z-[100] pointer-events-none md:flex-col md:items-center md:gap-[20px] md:pb-[25px] md:px-[15px]"
                    style={{
                        opacity: contentOpacity,
                        transform: `translateY(${contentOpacity === 1 ? '0' : '30px'})`,
                        transition: 'opacity 0.4s ease, transform 0.4s ease'
                    }}
                >
                     <div className="fullscreen-text-info pointer-events-auto text-white font-[Inter,sans-serif] text-shadow-[0_2px_10px_rgba(0,0,0,0.8)] max-w-[min(75%,600px)] bg-[rgba(255,255,255,0.1)] px-[18px] py-2.5 rounded-[14px] backdrop-blur-[15px] border-none md:max-w-full md:text-center">
                        <span className="fullscreen-counter font-[800] text-[#47b5ff] mr-3">{currentIndex + 1} / {images.length}</span>
                        {currentImage.title && <span className="fullscreen-title"> - {currentImage.title}</span>}
                        {!currentImage.title && currentImage.alt && <span className="fullscreen-title"> - {currentImage.alt}</span>}
                    </div>

                    {images.length > 1 && (
                        <div className="fullscreen-nav-buttons pointer-events-auto flex gap-[15px]">
                            <button className="fullscreen-nav-btn prev bg-[rgba(255,255,255,0.1)] border-none text-white cursor-pointer rounded-full backdrop-blur-[15px] transition-all duration-300 w-[46px] h-[46px] flex items-center justify-center hover:bg-[rgba(71,181,255,0.6)] hover:scale-110 disabled:opacity-20" onClick={handlePrev} aria-label="Previous">
                                <i className="fi fi-rr-arrow-small-left"></i>
                            </button>
                            <button className="fullscreen-nav-btn next bg-[rgba(255,255,255,0.1)] border-none text-white cursor-pointer rounded-full backdrop-blur-[15px] transition-all duration-300 w-[46px] h-[46px] flex items-center justify-center hover:bg-[rgba(71,181,255,0.6)] hover:scale-110 disabled:opacity-20" onClick={handleNext} aria-label="Next">
                                <i className="fi fi-rr-arrow-small-right"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(viewerContent, document.body);
};
export default FullScreenImageViewer;
